import { calcClubStandings } from '../../engine';
import { useT } from '../../i18n';

export interface ClubPadBannerProps {
  /** The club-format tournament whose live standings/leader are shown. */
  tournament: any;
  /** Compact styling when embedded inside a Banepad screen. */
  inPad?: boolean;
  /**
   * The live `Padel24Engine` wiring object (still defined in legacy/App.tsx). Read for
   * the per-format `calcClubStandings` resolver; falls back to the imported engine fn.
   */
  Padel24Engine: any;
  /**
   * Scoreboard computed elsewhere: `{ standings: { [clubId]: row }, leader }`.
   *
   * The club totals span every court (#328), which a pad fed by `/pad/state`
   * cannot see — so the server computes them. Omitted → computed here from
   * `tournament`, which is what the tournament view does.
   */
  precomputed?: { standings: any; leader: any } | null;
}

/**
 * Club-vs-club scoreboard banner for the Banepad / tournament view — shows both
 * clubs' totals, records and leader highlight. Extracted verbatim from the
 * monolith (legacy/App.tsx); markup/logic unchanged. Takes the live `Padel24Engine`
 * object as a prop because it resolves the per-format standings engine off it.
 */
export function ClubPadBanner({
  tournament,
  inPad = false,
  Padel24Engine,
  precomputed = null,
}: ClubPadBannerProps) {
  const t = useT();
  const clubs = tournament.config?.clubs || [];
  if (clubs.length !== 2) return null;
  const engine =
    tournament.type === 'club_team_americano'
      ? Padel24Engine.club_team_americano
      : tournament.type === 'club_mexicano'
        ? Padel24Engine.club_mexicano
        : Padel24Engine.club_americano;
  let standings = null,
    leader = null;
  if (precomputed) {
    standings = precomputed.standings;
    leader = precomputed.leader;
  } else
    try {
      const r =
        engine && engine.calcClubStandings
          ? engine.calcClubStandings(tournament)
          : calcClubStandings(tournament);
      standings = r.standings;
      leader = r.leader;
    } catch (_e) {
      // deviation from byte-identical: unused catch bindings renamed e/e2 → _e/_e2
      // to satisfy lint (@typescript-eslint/no-unused-vars); behaviour unchanged.
      try {
        const r = calcClubStandings(tournament);
        standings = r.standings;
        leader = r.leader;
      } catch (_e2) {
        return null;
      }
    }
  const a = standings?.[clubs[0].id];
  const b = standings?.[clubs[1].id];
  if (!a || !b) return null;
  const colorA = clubs[0]?.color || '#22c55e';
  const colorB = clubs[1]?.color || '#3b82f6';
  const initial = (name, fb) => (name || fb).trim().charAt(0).toUpperCase() || fb;
  const recordTxt = (st) =>
    t('tournament.clubPad.record', { wins: st.wins, losses: st.losses, ties: st.ties });
  return (
    <div
      className={inPad ? 'overflow-hidden flex-shrink-0' : 'rounded-2xl overflow-hidden mb-2'}
      style={{
        background: 'linear-gradient(165deg, #0a0b10 0%, #14161f 100%)',
        border: inPad ? undefined : '1px solid rgba(255,255,255,0.08)',
        borderBottom: inPad
          ? '1px solid rgba(194,240,47,0.18)'
          : '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <div
        className="grid items-center"
        style={{ gridTemplateColumns: '1fr auto 1fr', padding: '14px 18px', gap: 12 }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="flex items-center justify-center rounded-lg flex-shrink-0"
            style={{
              width: 42,
              height: 42,
              background: colorA,
              color: '#fff',
              fontWeight: 800,
              fontSize: 18,
            }}
          >
            {initial(clubs[0]?.name, 'A')}
          </div>
          <div className="min-w-0">
            <div
              className="font-display font-bold truncate"
              style={{ color: '#fff', fontSize: 16 }}
            >
              {clubs[0]?.name || t('tournament.clubPad.clubA')}
            </div>
            <div className="font-mono" style={{ color: 'rgba(230,251,255,0.55)', fontSize: 11 }}>
              {recordTxt(a)}
            </div>
          </div>
        </div>
        <div className="text-center" style={{ padding: '0 6px' }}>
          <div className="font-mono font-bold tabular-nums" style={{ fontSize: 30, lineHeight: 1 }}>
            <span style={{ color: leader === clubs[0]?.id ? colorA : '#ffffff' }}>
              {a.totalScore}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.35)', margin: '0 10px' }}>–</span>
            <span style={{ color: leader === clubs[1]?.id ? colorB : '#ffffff' }}>
              {b.totalScore}
            </span>
          </div>
          <div
            className="font-mono uppercase tracking-wider"
            style={{ color: 'rgba(230,251,255,0.4)', fontSize: 9, marginTop: 5 }}
          >
            {t('tournament.clubPad.matchesPlayed', { n: a.matchesPlayed })}
          </div>
        </div>
        <div className="flex items-center gap-3 min-w-0 justify-end">
          <div className="min-w-0 text-right">
            <div
              className="font-display font-bold truncate"
              style={{ color: '#fff', fontSize: 16 }}
            >
              {clubs[1]?.name || t('tournament.clubPad.clubB')}
            </div>
            <div className="font-mono" style={{ color: 'rgba(230,251,255,0.55)', fontSize: 11 }}>
              {recordTxt(b)}
            </div>
          </div>
          <div
            className="flex items-center justify-center rounded-lg flex-shrink-0"
            style={{
              width: 42,
              height: 42,
              background: colorB,
              color: '#fff',
              fontWeight: 800,
              fontSize: 18,
            }}
          >
            {initial(clubs[1]?.name, 'B')}
          </div>
        </div>
      </div>
    </div>
  );
}
